import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, X, Loader2, Save, Sparkles, Upload } from "lucide-react";
import { ProfileCompletion } from "@/components/ProfileCompletion";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";

const SectionTitle = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
);

function EntryDialog({ type, entry, onSaved }) {
  const isEdit = !!entry;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isWork = type === "work";
  const blank = isWork
    ? { company: "", role: "", start_date: "", end_date: "", description: "" }
    : { institution: "", degree: "", start_date: "", end_date: "", description: "" };
  const [form, setForm] = useState(entry || blank);

  useEffect(() => {
    if (open) setForm(entry || blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const path = isWork ? "work-experiences" : "educations";
      if (isEdit) await api.put(`/${path}/${entry.id}`, form);
      else await api.post(`/${path}`, form);
      toast.success(isEdit ? "Updated" : "Added");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error("Could not save entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-${type}-button`}>
            <Pencil className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        ) : (
          <Button variant="outline" size="sm" data-testid={`add-${type}-button`} className="active:scale-[0.98] transition-transform">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit" : "Add"} {isWork ? "Work Experience" : "Education"}</DialogTitle>
          <DialogDescription>Fill in the details below and save.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">{isWork ? "Company" : "Institution"}</Label>
            <Input value={isWork ? form.company : form.institution} onChange={(e) => set(isWork ? "company" : "institution", e.target.value)} data-testid={`${type}-org-input`} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">{isWork ? "Role" : "Degree"}</Label>
            <Input value={isWork ? form.role : form.degree} onChange={(e) => set(isWork ? "role" : "degree", e.target.value)} data-testid={`${type}-title-input`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Start</Label>
            <Input value={form.start_date} onChange={(e) => set("start_date", e.target.value)} placeholder="Jan 2022" data-testid={`${type}-start-input`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">End</Label>
            <Input value={form.end_date} onChange={(e) => set("end_date", e.target.value)} placeholder="Present" data-testid={`${type}-end-input`} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid={`${type}-desc-input`} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid={`save-${type}-button`} className="active:scale-[0.98] transition-transform">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [work, setWork] = useState([]);
  const [edu, setEdu] = useState([]);
  const [skills, setSkills] = useState([]);
  const [newSkill, setNewSkill] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [importInfo, setImportInfo] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);

  const load = async () => {
    const { data } = await api.get("/profile");
    setProfile(data.profile);
    setWork(data.work_experiences);
    setEdu(data.educations);
    setSkills(data.skills);
    return data;
  };

  useEffect(() => {
    (async () => {
      const data = await load();
      if ((data.work_experiences || []).length === 0 && localStorage.getItem("cv_import_dismissed") !== "1") {
        try {
          const { data: avail } = await api.get("/free-analysis/import-available");
          if (avail.available) setImportInfo(avail);
        } catch (e) { console.error("Import-available check failed:", e); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/free-analysis/import-profile", {}, { timeout: 180000 });
      if (data.imported) {
        await load();
        setImportInfo(null);
        toast.success("Imported details from your CV.");
      } else {
        toast.info("Couldn't find a CV to import.");
        setImportInfo(null);
      }
    } catch (e) {
      // CV parsing can take a while and the gateway may time out even though the
      // import completes server-side. Re-check the profile before declaring failure.
      try {
        const refreshed = await load();
        const filled = (refreshed.work_experiences || []).length > 0 ||
          (refreshed.educations || []).length > 0 ||
          (refreshed.skills || []).length > 0 ||
          !!(refreshed.profile && (refreshed.profile.summary || refreshed.profile.full_name));
        if (filled) {
          setImportInfo(null);
          toast.success("Imported details from your CV.");
          return;
        }
      } catch (e) { console.error("CV import refresh failed:", e); }
      toast.error("Import is taking longer than usual — please refresh in a moment to see your imported details.");
    } finally {
      setImporting(false);
    }
  };

  const dismissImport = () => {
    localStorage.setItem("cv_import_dismissed", "1");
    setImportInfo(null);
  };

  const uploadCvImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/import-cv-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      await load();
      toast.success("Imported details from your CV.");
    } catch (err) {
      // A slow parse can complete server-side even if the gateway times out.
      try {
        const refreshed = await load();
        const filled = (refreshed.work_experiences || []).length > 0 ||
          !!(refreshed.profile && (refreshed.profile.summary || refreshed.profile.full_name));
        if (filled) { toast.success("Imported details from your CV."); return; }
      } catch (e) { console.error("CV upload refresh failed:", e); }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : "Import is taking longer than usual — please refresh in a moment to see your details.");
    } finally {
      setUploadingCv(false);
    }
  };

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/profile", {
        full_name: profile.full_name, email: profile.email, phone: profile.phone,
        location: profile.location, summary: profile.summary,
        linkedin_url: profile.linkedin_url, portfolio_url: profile.portfolio_url,
        work_authorization: profile.work_authorization, requires_sponsorship: profile.requires_sponsorship,
        notice_period: profile.notice_period, desired_salary: profile.desired_salary,
      });
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Could not save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const removeEntry = async (type, id) => {
    const path = type === "work" ? "work-experiences" : "educations";
    await api.delete(`/${path}/${id}`);
    toast.success("Removed");
    load();
  };

  const addSkill = async (e) => {
    e.preventDefault();
    if (!newSkill.trim()) return;
    await api.post("/skills", { name: newSkill.trim() });
    setNewSkill("");
    load();
  };

  const removeSkill = async (id) => {
    await api.delete(`/skills/${id}`);
    load();
  };

  if (!profile) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">This information is used to generate your tailored CVs.</p>
      </div>

      <OnboardingChecklist />

      {importInfo && (
        <div className="flex items-center gap-3 bg-accent/5 border border-accent/20 rounded-xl p-4" data-testid="cv-import-banner">
          <Sparkles className="h-5 w-5 text-accent shrink-0" strokeWidth={1.75} />
          <p className="text-sm flex-1">
            We found a CV analysis{importInfo.target_role ? ` for ${importInfo.target_role}` : ""} linked to your email — import it to prefill your profile?
          </p>
          <Button size="sm" onClick={runImport} disabled={importing} data-testid="cv-import-btn">
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Import it
          </Button>
          <button onClick={dismissImport} className="text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="cv-import-dismiss"><X className="h-4 w-4" /></button>
        </div>
      )}

      {!importInfo && work.length === 0 && !(profile.summary || "").trim() && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-xl p-4" data-testid="cv-upload-import-banner">
          <Upload className="h-5 w-5 text-accent shrink-0" strokeWidth={1.75} />
          <div className="flex-1">
            <p className="text-sm font-medium">Already have a CV? Upload it to auto-fill your profile.</p>
            <p className="text-xs text-muted-foreground mt-0.5">We'll read your PDF or Word (.docx) CV and fill in your details — you can review and edit everything afterwards.</p>
          </div>
          <input id="cv-upload-input" type="file" accept=".pdf,.docx" className="hidden" onChange={uploadCvImport} data-testid="cv-upload-input" />
          <Button size="sm" disabled={uploadingCv} onClick={() => document.getElementById("cv-upload-input").click()} data-testid="cv-upload-import-btn" className="active:scale-[0.98] transition-transform">
            {uploadingCv ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Reading CV…</> : <><Upload className="h-4 w-4 mr-1.5" /> Upload CV</>}
          </Button>
        </div>
      )}

      <ProfileCompletion profile={profile} work={work} edu={edu} skills={skills} />

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader><CardTitle className="font-heading text-xl">Personal Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Full Name</Label>
            <Input value={profile.full_name || ""} onChange={(e) => setP("full_name", e.target.value)} data-testid="profile-fullname-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
            <Input value={profile.email || ""} onChange={(e) => setP("email", e.target.value)} data-testid="profile-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Phone</Label>
            <Input value={profile.phone || ""} onChange={(e) => setP("phone", e.target.value)} data-testid="profile-phone-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Location</Label>
            <Input value={profile.location || ""} onChange={(e) => setP("location", e.target.value)} data-testid="profile-location-input" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Professional Summary</Label>
            <Textarea rows={4} value={profile.summary || ""} onChange={(e) => setP("summary", e.target.value)} data-testid="profile-summary-input" />
          </div>
          <div className="md:col-span-2">
            <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-button" className="active:scale-[0.98] transition-transform">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Profile</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Application Details</CardTitle>
          <p className="text-sm text-muted-foreground">Used by the Quick-Apply helper to fill common ATS fields (Greenhouse, Workday, Lever).</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">LinkedIn URL</Label>
            <Input value={profile.linkedin_url || ""} onChange={(e) => setP("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" autoComplete="off" data-testid="profile-linkedin-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Website / Portfolio</Label>
            <Input value={profile.portfolio_url || ""} onChange={(e) => setP("portfolio_url", e.target.value)} placeholder="https://…" autoComplete="off" data-testid="profile-portfolio-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Work Authorization</Label>
            <Input value={profile.work_authorization || ""} onChange={(e) => setP("work_authorization", e.target.value)} placeholder="e.g. Authorized to work in the UK" data-testid="profile-workauth-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Requires Sponsorship?</Label>
            <Input value={profile.requires_sponsorship || ""} onChange={(e) => setP("requires_sponsorship", e.target.value)} placeholder="Yes / No" data-testid="profile-sponsorship-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Notice Period</Label>
            <Input value={profile.notice_period || ""} onChange={(e) => setP("notice_period", e.target.value)} placeholder="e.g. 1 month" data-testid="profile-notice-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Desired Salary</Label>
            <Input value={profile.desired_salary || ""} onChange={(e) => setP("desired_salary", e.target.value)} placeholder="e.g. £65,000" data-testid="profile-salary-input" />
          </div>
          <div className="md:col-span-2">
            <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-application-details-button" className="active:scale-[0.98] transition-transform">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Application Details</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-heading text-xl">Work Experience</CardTitle>
            <EntryDialog type="work" onSaved={load} />
          </CardHeader>
          <CardContent className="space-y-3">
            {work.length === 0 && <p className="text-sm text-muted-foreground">No work experience yet.</p>}
            {work.map((w) => (
              <div key={w.id} className="rounded-md border border-zinc-200 p-4" data-testid={`work-item-${w.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{w.role} <span className="text-muted-foreground font-normal">· {w.company}</span></p>
                    <SectionTitle>{w.start_date} — {w.end_date}</SectionTitle>
                  </div>
                  <div className="flex shrink-0">
                    <EntryDialog type="work" entry={w} onSaved={load} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeEntry("work", w.id)} data-testid={`delete-work-${w.id}`}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                {w.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{w.description}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="font-heading text-xl">Education</CardTitle>
            <EntryDialog type="education" onSaved={load} />
          </CardHeader>
          <CardContent className="space-y-3">
            {edu.length === 0 && <p className="text-sm text-muted-foreground">No education yet.</p>}
            {edu.map((ed) => (
              <div key={ed.id} className="rounded-md border border-zinc-200 p-4" data-testid={`edu-item-${ed.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{ed.degree} <span className="text-muted-foreground font-normal">· {ed.institution}</span></p>
                    <SectionTitle>{ed.start_date} — {ed.end_date}</SectionTitle>
                  </div>
                  <div className="flex shrink-0">
                    <EntryDialog type="education" entry={ed} onSaved={load} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeEntry("education", ed.id)} data-testid={`delete-edu-${ed.id}`}>
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                {ed.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{ed.description}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader><CardTitle className="font-heading text-xl">Skills</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={addSkill} className="flex gap-2 mb-4 max-w-md">
            <Input value={newSkill} onChange={(e) => setNewSkill(e.target.value)} placeholder="Add a skill…" data-testid="skill-input" />
            <Button type="submit" data-testid="add-skill-button" className="active:scale-[0.98] transition-transform">
              <Plus className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {skills.length === 0 && <p className="text-sm text-muted-foreground">No skills yet.</p>}
            {skills.map((s) => (
              <Badge key={s.id} variant="secondary" className="pl-3 pr-1.5 py-1.5 text-sm gap-1" data-testid={`skill-${s.id}`}>
                {s.name}
                <button onClick={() => removeSkill(s.id)} className="rounded-full hover:bg-zinc-300 p-0.5" data-testid={`remove-skill-${s.id}`}>
                  <X className="h-3 w-3" strokeWidth={2} />
                </button>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
